/* eslint-disable react-hooks/rules-of-hooks */
import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useConfig, useCookie, getEventItemFromProduct } from 'msp-integrations'
import Headline from '@atoms/Headline/Headline'
import AlertBox from '@molecules/AlertBox/AlertBox'
import WishlistButton from '@molecules/WishlistButton/WishlistButton'
import { getTotalCustomerCreditUsedInItems } from '@utils/basketUtils'
import MiniCartProduct from './MiniCartProduct'
import MiniCartTotal from './MiniCartTotal'
import MiniCartButtons from './MiniCartButtons'
import * as P from './MiniCart.partials'
import * as T from './MiniCart.types'
import {
  BonusDiscountLineItem,
  CouponItem
} from '@molecules/LineItem/LineItemPromotion.types'
import ProgressBar from '@molecules/ProgressBar/ProgressBar'
import MinimumOrderMessage from '@molecules/MinimumOrderMessage/MinimumOrderMessage'
import { twMerge } from 'tailwind-merge'

const MiniCart = ({
  basket,
  fallBackImage,
  productsQuantity,
  onClose,
  handleBonusDiscountLineItem = () => {},
  productUrlBuilder,
  buildUrl,
  GoToCartButton,
  GoToShopButton,
  inline = false,
  inCartPage = false,
  onWishlistChange,
  minAmountFreeShipping,
  minBoxesFreeDelivery
}: T.MiniCartProps): React.ReactElement => {
  if (!basket) {
    return <P.ErrorBasketUnknown />
  }

  const {
    theme: {
      font,
      mspComponents: { wishlistButton }
    }
  } = useConfig()

  const { eventTracking } = useCookie()
  const alertBoxRef = useRef<typeof AlertBox>()
  const [handleAddToCartError, setHandleAddToCartError] = useState<{
    [k: string]: any
  }>({})
  const { formatMessage } = useIntl()
  const {
    c_categories,
    c_containsVGC,
    c_groupedByCategories,
    c_isShippingFree,
    c_minBoxesOrder,
    c_quantityOfBrews,
    c_remainingBrewsToMinimum,
    orderTotal,
    productTotal
  } = basket

  // For showMinimumOrderMessage
  const hasRemainingBrews = c_remainingBrewsToMinimum > 0
  const hasMinimumQuantityOfBrews = c_quantityOfBrews > 0
  const hasMinimumBoxesOrder = c_minBoxesOrder !== 0
  const showMinimumOrderMessage =
    hasRemainingBrews && hasMinimumQuantityOfBrews && hasMinimumBoxesOrder

  // For showProgressBar
  const hasQuantityOfBrews = c_quantityOfBrews > 0
  const exceedsMinAmountForFreeShipping =
    (orderTotal || productTotal) > minAmountFreeShipping
  const showProgressBar =
    hasQuantityOfBrews ||
    (exceedsMinAmountForFreeShipping && !c_containsVGC && !inCartPage)

  const onQuantityChange = (product: any) => {
    return (quantity: number) => {
      basket
        .updateItemInBasket(
          {
            productId: product.productId,
            quantity,
            price: product.price
          },
          product.itemId
        )
        .then((response: any) => {
          if (response?.isError) {
            const { statusCode = '', statusMessage = '' } = response.arguments

            setHandleAddToCartError({
              errorCode: statusCode.toLowerCase(),
              alertMessage: statusMessage,
              isError: true
            })
            // @ts-ignore
            alertBoxRef?.current?.showAlert()
          }
        })
    }
  }

  const onRemoveItemFromBasket = (product: any) => {
    const quantity =
      basket?.productItems?.find(
        (item: any) => item?.productId === product?.productId
      )?.quantity || 1
    basket.removeItemFromBasket(product.itemId)
    eventTracking('remove_from_cart', {
      ecommerce: {
        currency: basket?.currency,
        items: [getEventItemFromProduct(product, quantity)]
      }
    })
  }

  const bonusItemsByCouponCode = new Map<string, BonusDiscountLineItem>()
  ;(basket?.bonusDiscountLineItems || []).forEach(
    (bonusItem: BonusDiscountLineItem) => {
      if (bonusItem.couponCode) {
        ;(basket?.couponItems || []).forEach((couponItem: CouponItem) => {
          if (couponItem.code === bonusItem.couponCode) {
            bonusItemsByCouponCode.set(
              bonusItem.couponCode as string,
              bonusItem
            )
          }
        })
      }
    }
  )
  let customerCreditUsedItems: number = 0

  if (basket?.c_customerCreditUsed) {
    customerCreditUsedItems = getTotalCustomerCreditUsedInItems(basket)
  }

  return (
    <div
      className={twMerge(
        font.regular,
        !inCartPage && 'h-full',
        'flex w-screen flex-col overflow-auto bg-white md:w-full'
      )}
    >
      {basket && (
        <>
          <div className='grid grid-flow-row overflow-auto'>
            {showMinimumOrderMessage || showProgressBar ? (
              <div className='text-pineGreen border-b-4 border-solid border-black'>
                {showMinimumOrderMessage ? (
                  <MinimumOrderMessage
                    inCartPage={inCartPage}
                    remainingBrewsToMinimum={c_remainingBrewsToMinimum}
                  />
                ) : null}
                {showProgressBar ? (
                  <ProgressBar
                    basketTotalPrice={basket.productTotal}
                    minAmountFreeShipping={minAmountFreeShipping}
                    isFreeShipping={c_isShippingFree}
                    quantityOfBrews={c_quantityOfBrews}
                    targetBrewBoxes={minBoxesFreeDelivery}
                  />
                ) : null}
              </div>
            ) : null}
            {productsQuantity === 0 && <P.ErrorBasketEmpty />}
            {(c_categories || []).map((category: string) => {
              const catItem = c_groupedByCategories[category]

              return (
                <div key={catItem.categoryId}>
                  <Headline
                    tag='h2'
                    className={
                      inCartPage
                        ? 'headline-h5 py-5 pl-6 capitalize md:pl-0'
                        : 'text-20 tracking-0.6 headline-h5 px-10 pb-5 pt-10 capitalize'
                    }
                  >
                    {catItem?.categoryName || ''}
                  </Headline>
                  <div>
                    {(catItem?.products || []).map((product: any) => (
                      <MiniCartProduct
                        key={product.itemId}
                        product={{
                          ...product,
                          currency: basket.currency
                        }}
                        fallBackImage={fallBackImage}
                        getProductDetail={basket.getProductDetail}
                        productUrlBuilder={productUrlBuilder}
                        removePromoCode={basket.removePromoCode}
                        handleBonusDiscountLineItem={(
                          bonusDiscountLineItem:
                            | BonusDiscountLineItem
                            | undefined
                        ) => handleBonusDiscountLineItem(bonusDiscountLineItem)}
                        bonusItemsByCouponCode={bonusItemsByCouponCode}
                        couponItems={basket.couponItems}
                        inline={inline}
                        buildUrl={buildUrl}
                        onDelete={() => onRemoveItemFromBasket(product)}
                        onQuantityChange={onQuantityChange(product)}
                        WishlistButton={
                          <WishlistButton
                            product={product}
                            isDisabled={!product.c_purchasable}
                            wishlistedContainerStyle={wishlistButton}
                            onWishlistChange={onWishlistChange}
                          />
                        }
                      />
                    ))}
                  </div>
                </div>
              )
            })}
            <div>
              {basket?.giftCertificateItems &&
              basket?.giftCertificateItems?.length > 0 ? (
                <Headline
                  tag='h2'
                  className={
                    inCartPage
                      ? `${font.medium} headline-h5 py-5 pl-6 capitalize md:pl-0`
                      : `${font.regular} text-20 tracking-0.6 headline-h5 px-10 pb-5 pt-10 capitalize`
                  }
                >
                  {formatMessage({
                    defaultMessage: 'Gift Cards',
                    id: 'msp.list.groups.giftcards.title'
                  })}
                </Headline>
              ) : (
                <></>
              )}
              {(basket?.giftCertificateItems || []).map(
                (giftCertificateItem: any) => {
                  let product = {
                    itemId: giftCertificateItem.giftCertificateItemId,
                    productId: giftCertificateItem.c_originalSKU,
                    productName: giftCertificateItem.c_productName
                  }

                  return (
                    <MiniCartProduct
                      key={product.itemId}
                      product={{
                        ...product,
                        currency: basket.currency
                      }}
                      fallBackImage={fallBackImage}
                      getProductDetail={basket.getProductDetail}
                      productUrlBuilder={productUrlBuilder}
                      couponItems={[]}
                      inline={inline}
                      buildUrl={buildUrl}
                      onDelete={() =>
                        basket.removeGiftCertificateItemFromBasket(
                          product.itemId
                        )
                      }
                      onQuantityChange={() => {}}
                    />
                  )
                }
              )}
            </div>
            {productsQuantity > 0 && (
              <MiniCartTotal
                total={basket.itemTotal}
                orderPriceAdjustments={basket.orderPriceAdjustments}
                currency={basket.currency}
                removePromoCode={basket.removePromoCode}
                handleBonusDiscountLineItem={(
                  bonusDiscountLineItem: BonusDiscountLineItem | undefined
                ) => handleBonusDiscountLineItem(bonusDiscountLineItem)}
                bonusItemsByCouponCode={bonusItemsByCouponCode}
                couponItems={basket.couponItems}
                inline={inline}
                customerCreditUsedForItems={-Math.abs(customerCreditUsedItems)}
                minAmountFreeShipping={minAmountFreeShipping}
                minBoxesFreeDelivery={minBoxesFreeDelivery}
              />
            )}
            <MiniCartButtons
              GoToCartButton={GoToCartButton}
              GoToShopButton={GoToShopButton}
              onClose={onClose}
              hasItems={productsQuantity > 0}
            />
          </div>
          <AlertBox
            ref={alertBoxRef}
            description={handleAddToCartError.alertMessage}
            autoCloseTime={4000}
            onClose={() => setHandleAddToCartError({})}
          />
        </>
      )}
    </div>
  )
}

export default MiniCart
